// src/services/chatroomService.js
import axios from 'axios';
import { getApiUrl, getCsrfToken, getWebSocketUrl, logger } from './utils';

class ChatroomService {
  constructor() {
    // HTTP client
    this.client = axios.create({
      baseURL: getApiUrl(),
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Add request interceptor for CSRF
    this.client.interceptors.request.use(config => {
      if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
        const token = getCsrfToken();
        if (token) {
          config.headers['X-CSRFToken'] = token;
        }
      }
      return config;
    });

    // Add response interceptor for data unwrapping
    this.client.interceptors.response.use(
      response => {
        // Unwrap standardized {data: ...} format
        if (response.data && response.data.data !== undefined) {
          response.data = response.data.data;
        }
        return response;
      },
      error => Promise.reject(error)
    );

    // WebSocket
    this.ws = null;
    this.wsHandlers = {
      message: new Set(),
      open: new Set(),
      close: new Set(),
      error: new Set()
    };
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 3000;
    this.currentChannelId = null;
    this._intentionalDisconnect = false;
  }

  // ==================== HTTP API Methods ====================
  // Note: Auth methods are in allauthApi.js (separation of concerns)

  async getChannels() {
    try {
      const response = await this.client.get('/chatrooms/channels/');
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to fetch channels', error);
      throw error;
    }
  }

  async getChannel(channelId) {
    try {
      const response = await this.client.get(`/chatrooms/channels/${channelId}/`);
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to fetch channel', error);
      throw error;
    }
  }

  async getMessages(channelId, cursor = null) {
    try {
      const params = cursor ? { cursor } : {};
      const response = await this.client.get(
        `/chatrooms/channels/${channelId}/messages/`,
        { params }
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to fetch messages', error);
      throw error;
    }
  }

  async sendMessage(channelId, content, { parentMessageId = null, skipBidTrigger = false } = {}) {
    try {
      const data = { content };
      if (parentMessageId) {
        data.parent_message = parentMessageId;
      }
      if (skipBidTrigger) {
        data.skip_bid_trigger = true;
      }

      const response = await this.client.post(
        `/chatrooms/channels/${channelId}/messages/`,
        data
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to send message', error);
      throw error;
    }
  }

  async updateChannelSettings(channelId, settings) {
    try {
      const response = await this.client.patch(
        `/chatrooms/channels/${channelId}/settings/`,
        settings
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to update channel settings', error);
      throw error;
    }
  }

  async pauseChannel(channelId) {
    try {
      const response = await this.client.post(
        `/chatrooms/channels/${channelId}/pause/`
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to pause channel', error);
      throw error;
    }
  }

  async unpauseChannel(channelId) {
    try {
      const response = await this.client.post(
        `/chatrooms/channels/${channelId}/unpause/`
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to unpause channel', error);
      throw error;
    }
  }

  // ==================== Channel Creation Methods ====================

  async createChannel(name, description = '', settings = {}) {
    try {
      const response = await this.client.post('/chatrooms/channels/', {
        name,
        description,
        settings
      });
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to create channel', error);
      throw error;
    }
  }

  async getPersonas() {
    try {
      const response = await this.client.get('/chatrooms/personas/');
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to fetch personas', error);
      throw error;
    }
  }

  async addPersonaToChannel(channelId, personaId, role = 'writer') {
    try {
      const response = await this.client.post(
        `/chatrooms/channels/${channelId}/participants/`,
        { persona_id: personaId, role }
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to add persona to channel', error);
      throw error;
    }
  }

  // ==================== Participant Management Methods ====================

  async getParticipants(channelId) {
    try {
      const response = await this.client.get(`/chatrooms/channels/${channelId}/participants/`);
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to fetch participants', error);
      throw error;
    }
  }

  async inviteParticipant(channelId, { userId, role = 'writer' }) {
    try {
      const response = await this.client.post(
        `/chatrooms/channels/${channelId}/participants/`,
        { user_id: userId, role }
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to invite participant', error);
      throw error;
    }
  }

  async updateParticipant(channelId, participantId, { role }) {
    try {
      const response = await this.client.patch(
        `/chatrooms/participants/${participantId}/`,
        { role }
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to update participant', error);
      throw error;
    }
  }

  async removeParticipant(channelId, participantId) {
    try {
      await this.client.delete(`/chatrooms/participants/${participantId}/`);
      return true;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to remove participant', error);
      throw error;
    }
  }

  async leaveChannelAsParticipant(channelId, participantId) {
    try {
      await this.client.post(`/chatrooms/participants/${participantId}/leave/`);
      return true;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to leave channel', error);
      throw error;
    }
  }

  async acceptInvitation(channelId, participantId) {
    try {
      const response = await this.client.patch(
        `/chatrooms/participants/${participantId}/`,
        { invitation_accepted: true }
      );
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to accept invitation', error);
      throw error;
    }
  }

  async declineInvitation(channelId, participantId) {
    try {
      // Declining removes the participant record
      await this.client.delete(`/chatrooms/participants/${participantId}/`);
      return true;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to decline invitation', error);
      throw error;
    }
  }

  async searchInvitableUsers(channelId, search = '') {
    try {
      const params = { channel: channelId };
      if (search) {
        params.search = search;
      }
      const response = await this.client.get('/chatrooms/users/invitable/', { params });
      return response.data;
    } catch (error) {
      logger.error('ChatroomService', 'Failed to search invitable users', error);
      throw error;
    }
  }

  // ==================== WebSocket Methods ====================

  connect() {
    // Clear any previous disconnect intent - we want to connect now
    this._intentionalDisconnect = false;

    // Already connected or connecting
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      logger.debug('ChatroomService', 'WebSocket already connected');
      return;
    }

    try {
      const url = getWebSocketUrl();
      logger.info('ChatroomService', 'Connecting to WebSocket', { url });

      this.ws = new WebSocket(url);

      this.ws.onopen = (event) => {
        logger.info('ChatroomService', 'WebSocket connected');
        this.reconnectAttempts = 0;
        this._notifyHandlers('open', event);

        // Rejoin channel if we were in one
        if (this.currentChannelId) {
          this.joinChannel(this.currentChannelId);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          logger.debug('ChatroomService', 'WebSocket message received', { type: data.type });
          this._notifyHandlers('message', data);
        } catch (error) {
          logger.error('ChatroomService', 'Failed to parse WebSocket message', error);
        }
      };

      this.ws.onerror = (event) => {
        logger.error('ChatroomService', 'WebSocket error', event);
        this._notifyHandlers('error', event);
      };

      this.ws.onclose = (event) => {
        logger.info('ChatroomService', 'WebSocket closed', { code: event.code });
        this._notifyHandlers('close', event);

        // Don't reconnect if it was intentional
        if (this._intentionalDisconnect) {
          this._intentionalDisconnect = false;
          return;
        }

        // Attempt reconnection
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          logger.info('ChatroomService', `Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.connect(), this.reconnectDelay);
        }
      };

    } catch (error) {
      logger.error('ChatroomService', 'Failed to create WebSocket', error);
    }
  }

  disconnect() {
    this._intentionalDisconnect = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.currentChannelId = null;
  }

  joinChannel(channelId) {
    // Always track the channel we want to be in - reconnect logic uses this
    this.currentChannelId = channelId;

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.info('ChatroomService', 'WebSocket not connected, will join channel on connect', { channelId });
      return;
    }
    this.send({
      type: 'join_channel',
      channel_id: channelId
    });

    logger.info('ChatroomService', 'Joining channel', { channelId });
  }

  leaveChannel(channelId) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.send({
      type: 'leave_channel',
      channel_id: channelId
    });

    if (this.currentChannelId === channelId) {
      this.currentChannelId = null;
    }

    logger.info('ChatroomService', 'Leaving channel', { channelId });
  }

  sendTypingIndicator(channelId, isTyping) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.send({
      type: isTyping ? 'typing_start' : 'typing_stop',
      channel_id: channelId
    });
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      logger.error('ChatroomService', 'Cannot send - WebSocket not connected');
    }
  }

  // Handler registration
  on(event, handler) {
    if (this.wsHandlers[event]) {
      this.wsHandlers[event].add(handler);
    }
  }

  off(event, handler) {
    if (this.wsHandlers[event]) {
      this.wsHandlers[event].delete(handler);
    }
  }

  _notifyHandlers(event, data) {
    if (this.wsHandlers[event]) {
      this.wsHandlers[event].forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          logger.error('ChatroomService', `Error in ${event} handler`, error);
        }
      });
    }
  }
}

// Export singleton instance
export const chatroomService = new ChatroomService();
export default chatroomService;
